import { Fragment } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Markdown from 'react-markdown';
import type { ReactNode } from 'react';
import { getBlogPost, blogPosts, parseLocalDate } from '../data/blog-posts';
import { AdUnit } from '../components/AdUnit';
import { POST_IMAGES } from '../data/post-assets';
import './BlogPost.css';

// Split markdown content at the first blockquote block
function splitContent(content: string): { body: string; story: string } {
  const idx = content.search(/^>/m);
  if (idx === -1) return { body: content, story: '' };
  return {
    body: content.slice(0, idx).trim(),
    story: content.slice(idx).trim(),
  };
}

function readingTime(content: string): number {
  return Math.max(1, Math.round(content.trim().split(/\s+/).length / 200));
}

// Split markdown into chunks of N blocks for mobile paragraph ads
function splitIntoChunks(markdown: string, size: number): string[] {
  const blocks = markdown.split(/\n\n+/).filter(b => b.trim());
  const chunks: string[] = [];
  for (let i = 0; i < blocks.length; i += size) {
    chunks.push(blocks.slice(i, i + size).join('\n\n'));
  }
  return chunks;
}

// Hero zone: blockquote renders as the story panel (no card styling — just text)
const storyComponents = {
  blockquote({ children }: { children?: ReactNode }) {
    return <div className="blog-post__story">{children}</div>;
  },
};

// Body: blockquote renders as a styled callout card (for any in-body blockquotes)
const bodyComponents = {
  blockquote({ children }: { children?: ReactNode }) {
    return <div className="blog-post__callout">{children}</div>;
  },
};

export function BlogPost() {
  const { id } = useParams<{ id: string }>();
  const post = id ? getBlogPost(id) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const currentIndex = blogPosts.findIndex(p => p.id === post.id);
  // Array is newest-first, so older = higher index, newer = lower index
  const prevPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;

  const { body, story } = splitContent(post.content);
  const mins = readingTime(post.content);
  const hasHero = !!(post.image && POST_IMAGES[post.image]);
  const bodyChunks = splitIntoChunks(body, 3);

  const formattedDate = parseLocalDate(post.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <article className="blog-post">

      {/* ── Header: back link + title + meta + tags ── */}
      <header className="blog-post__header">
        <div className="container container--narrow">
          <Link to="/blog" className="blog-post__back">
            <i className="fas fa-arrow-left"></i>
            Back to Blog
          </Link>
          {post.series && (
            <div className="blog-post__series-badge">
              <i className="fas fa-layer-group" />
              {post.series.name} &middot; No.&nbsp;{post.series.part}
            </div>
          )}
          <h1>{post.title}</h1>
          <div className="blog-post__meta">
            <span className="blog-post__date">
              <i className="far fa-calendar"></i>
              {formattedDate}
            </span>
            <span className="blog-post__author">
              <i className="far fa-user"></i>
              {post.author}
            </span>
            <span className="blog-post__read-time">
              <i className="far fa-clock"></i>
              {mins} min read
            </span>
          </div>
          <div className="blog-post__tags">
            {post.tags.map(tag => (
              <span key={tag} className="blog-post__tag">{tag}</span>
            ))}
          </div>
        </div>
      </header>

      {/* ── Article body + sidebars ── */}
      <div className="blog-post__content">
        <div className="blog-post__layout">

          {/* Article — center column */}
          <div className="blog-post__main">
            {/* Repeated title + byline as article intro */}
            <div className="blog-post__article-intro">
              <h2 className="blog-post__article-title">{post.title}</h2>
              <div className="blog-post__article-byline">
                <span>{post.author}</span>
                <span className="blog-post__byline-dot" aria-hidden="true">·</span>
                <span>{formattedDate}</span>
                <span className="blog-post__byline-dot" aria-hidden="true">·</span>
                <span>{mins} min read</span>
              </div>
            </div>

            {/* Full-width article image — between intro and body */}
            {hasHero && (
              <div className="blog-post__article-image">
                <img
                  src={POST_IMAGES[post.image!]}
                  alt={post.title}
                  className="blog-post__article-img"
                />
              </div>
            )}

            {/* Main article body — mobile ads between chunks */}
            <div className="blog-post__body">
              {bodyChunks.map((chunk, i) => (
                <Fragment key={i}>
                  <Markdown components={bodyComponents}>{chunk}</Markdown>
                  {i < bodyChunks.length - 1 && (
                    <div className="blog-post__ad-wrap blog-post__ad-wrap--paragraph">
                      <AdUnit slot="6240382661" format="auto" />
                    </div>
                  )}
                </Fragment>
              ))}
            </div>

            {/* Post-body inline ad */}
            <div className="blog-post__ad-wrap blog-post__ad-wrap--inline">
              <AdUnit slot="6240382661" format="auto" />
            </div>

            {/* Quiet Reminder — narrative story, pinned to the bottom */}
            {story && (
              <div className="blog-post__quiet-reminder">
                <Markdown components={storyComponents}>{story}</Markdown>
              </div>
            )}
          </div>

          {/* Left sidebar — desktop only */}
          <aside className="blog-post__sidebar blog-post__sidebar--left">
            <div className="blog-post__sidebar-sticky">
              <div className="blog-post__ad-wrap blog-post__ad-wrap--sidebar">
                <AdUnit slot="4412800250" format="rectangle" responsive={false} />
              </div>
              <div className="blog-post__ad-wrap blog-post__ad-wrap--sidebar">
                <AdUnit slot="4412800250" format="rectangle" responsive={false} />
              </div>
            </div>
          </aside>

          {/* Right sidebar — desktop only */}
          <aside className="blog-post__sidebar blog-post__sidebar--right">
            <div className="blog-post__sidebar-sticky">
              <div className="blog-post__ad-wrap blog-post__ad-wrap--sidebar">
                <AdUnit slot="4412800250" format="rectangle" responsive={false} />
              </div>
              <div className="blog-post__ad-wrap blog-post__ad-wrap--sidebar">
                <AdUnit slot="4412800250" format="rectangle" responsive={false} />
              </div>
            </div>
          </aside>

        </div>
      </div>

      {/* ── Footer: share + prev/next nav ── */}
      <footer className="blog-post__footer">
        <div className="container container--narrow">
          <div className="blog-post__share">
            <span>Share this article:</span>
            <div className="blog-post__share-links">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Twitter"
              >
                <i className="fab fa-x-twitter"></i>
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Facebook"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on LinkedIn"
              >
                <i className="fab fa-linkedin-in"></i>
              </a>
            </div>
          </div>

          <nav className="blog-post__nav">
            {prevPost ? (
              <Link to={`/blog/${prevPost.id}`} className="blog-post__nav-link blog-post__nav-link--prev">
                <span className="blog-post__nav-label">
                  <i className="fas fa-arrow-left"></i>
                  Previous{prevPost.series ? ` · No. ${prevPost.series.part}` : ''}
                </span>
                <span className="blog-post__nav-title">{prevPost.title}</span>
              </Link>
            ) : <div />}
            {nextPost ? (
              <Link to={`/blog/${nextPost.id}`} className="blog-post__nav-link blog-post__nav-link--next">
                <span className="blog-post__nav-label">
                  Next{nextPost.series ? ` · No. ${nextPost.series.part}` : ''}
                  <i className="fas fa-arrow-right"></i>
                </span>
                <span className="blog-post__nav-title">{nextPost.title}</span>
              </Link>
            ) : <div />}
          </nav>
        </div>
      </footer>

    </article>
  );
}
